import { Box, Divider, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import AppContext from '../../../context/appContext'
import { useReportTemplates } from '../../../hooks/data/useReportTemplates'
import { ruutsApi } from '../../../services/ruutsApi'
import { userRoles as USER_ROLES } from '../../../utils/userRoles'
import { AttestationReportActions } from '../Attestation/AttestationReportActions'
import AttestationReportForm from '../Attestation/AttestationReportForm'
import DynamicReportForm from '../DynamicReportForm'
import { TemplateReportActions } from '../TemplateReportActions'
import { ReportsTable } from './ReportsTable'

// Router state is structured-cloned, which strips the Dayjs prototype and leaves the
// report page's .format() calls on a plain object; ISO strings survive the round trip.
const serializeDayjsFields = (data: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      dayjs.isDayjs(value) ? value.toISOString() : value,
    ]),
  )

interface ReportType {
  name: string
  component: React.ReactNode
  actions: React.ReactNode
  roles: string[]
}

export const ReportsHome = () => {
  const navigate = useNavigate()
  const { currentFarm, programConfig, userRoleSelected } = useContext(AppContext)
  const { data: reportTemplates } = useReportTemplates()
  const [selectedReportType, setSelectedReportType] = useState('')
  const [displayAddForm, setDisplayAddForm] = useState(false)

  const submitReport = useCallback(
    (data: Record<string, unknown>) => {
      const report = {
        ...serializeDayjsFields(data),
        type: selectedReportType,
        farm: currentFarm,
      }
      navigate(`/reports/${selectedReportType}/new`, { state: { report } })
    },
    [currentFarm, navigate, selectedReportType],
  )

  const submitTemplateReport = useCallback(
    async (data: Record<string, unknown> & { reportId: string }) => {
      const { reportId, ...query } = data
      await ruutsApi.reports.getReport({ reportId, query })
    },
    [],
  )

  const handleCancel = useCallback(() => {
    setDisplayAddForm(false)
  }, [setDisplayAddForm])

  const REPORT_TYPES = useMemo(
    () => ({
      Attestation: {
        name: 'Attestation',
        component: <AttestationReportForm onCancel={handleCancel} onSubmit={submitReport} />,
        actions: <AttestationReportActions setDisplayAddForm={setDisplayAddForm} />,
        roles: [USER_ROLES.Admin, USER_ROLES.Analyst],
      },
    }),
    [handleCancel, setDisplayAddForm, submitReport],
  )

  const allReports = useMemo(() => {
    const baseReports: Record<string, ReportType> = { ...REPORT_TYPES }

    if (reportTemplates) {
      reportTemplates.forEach(template => {
        if (template.roles.includes(userRoleSelected as string)) {
          baseReports[template.id] = {
            name: template.name,
            component: (
              <DynamicReportForm
                formDefinitionNamespace={template.formDefinitionNamespace}
                reportTemplate={template}
                onCancel={handleCancel}
                onSubmit={submitTemplateReport}
              />
            ),
            actions: (
              <TemplateReportActions
                setDisplayAddForm={() => {
                  setDisplayAddForm(true)
                }}
              />
            ),
            roles: template.roles,
          }
        }
      })
    }

    return baseReports
  }, [REPORT_TYPES, reportTemplates, userRoleSelected, handleCancel, submitTemplateReport])

  const filteredReports = useMemo(() => {
    const enabledReports = programConfig?.enabledReports || []
    const reports: Record<string, ReportType> = {}

    enabledReports.forEach(type => {
      if (allReports[type] && allReports[type].roles.includes(userRoleSelected)) {
        reports[type] = allReports[type]
      }
    })

    return reports
  }, [programConfig?.enabledReports, allReports, userRoleSelected])

  if (!currentFarm || Object.keys(filteredReports).length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          marginTop: '16px',
        }}
      >
        <Typography variant="h6">No hay reportes disponibles</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        padding: '16px',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <FormControl fullWidth>
        <InputLabel id="report-type-select-label">Tipo de reporte</InputLabel>
        <Select
          id="report-type-select"
          label="Tipo de reporte"
          labelId="report-type-select-label"
          value={selectedReportType}
          onChange={event => setSelectedReportType(event.target.value)}
        >
          {Object.entries(filteredReports)
            .sort((a, b) => a[1].name.localeCompare(b[1].name))
            .map(([key, report]) => (
              <MenuItem key={report.name} value={key}>
                {report.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <Divider />
      {displayAddForm ? (
        filteredReports[selectedReportType]?.component
      ) : (
        <ReportsTable farmId={currentFarm?.id} reportType={selectedReportType}>
          <ReportsTable.Actions>
            {filteredReports[selectedReportType]?.actions}
          </ReportsTable.Actions>
        </ReportsTable>
      )}
    </Box>
  )
}
